import React, { useState } from "react";
import {
  Box,
  Button,
  Flex,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Input,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Center,
  Image,
  Text,
  useToast,
  Select,
  HStack,
} from "@chakra-ui/react";
import { useNavigate, useParams } from "react-router-dom";
import { useGetSightseensQuery } from "../queries/tourAreaQueries";
import Loading from "../../../utilities/Loading";
import Error from "../../../utilities/Error";

// SightseenForm component has been moved to its own file: AddSightseen.jsx

// Helper function to render HTML content safely
const createMarkup = (htmlContent) => {
  return { __html: htmlContent };
};

const Sightseen = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const toast = useToast();
  const [searchText, setSearchText] = useState("");
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  
  // Fetch sightseen data for this tour area
  const {
    data: sightseenData,
    error,
    isLoading,
    isFetching,
    refetch
  } = useGetSightseensQuery(id);
  
  // Filter the data based on search input
  const filteredData = sightseenData?.data?.filter(item =>
    item.heading?.toLowerCase().includes(searchText.toLowerCase())
  ) || [];
  
  // Calculate pagination
  const indexOfLastEntry = currentPage * entriesPerPage;
  const indexOfFirstEntry = indexOfLastEntry - entriesPerPage;
  const currentEntries = filteredData.slice(indexOfFirstEntry, indexOfLastEntry);
  const totalPages = Math.ceil(filteredData.length / entriesPerPage);
  
  // Handle page change
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  // Show loading state
  if (isLoading || isFetching) {
    return (
      <Center mt={2} minH="100vh" p={3} bg="white" borderRadius="md">
        <Loading />
      </Center>
    );
  }

  // Show error state
  if (error) {
    return (
      <Center mt={2} minH="100vh" p={3} bg="white" borderRadius="md">
        <Error />
      </Center>
    );
  }

  return (
    <Box p={6} boxShadow="md" borderRadius="lg" bg="white" my="60px">
      <Flex justify="space-between" align="center" mb={6}>
        <Heading as="h1" size="lg">Sightseen</Heading>
        <Button 
          colorScheme="blue" 
          bg="var(--chakra-colors-mainBlue)" 
          onClick={() => navigate(`/tour-area/${id}/sightseen/add`)}
        >
          Add
        </Button>
      </Flex>
      <Flex justify="space-between" align="center" mb={4}>
        <Box display="flex" alignItems="center" gap={2}>
          <Box>Show</Box>
          <Select 
            value={entriesPerPage} 
            onChange={(e) => {
              setEntriesPerPage(Number(e.target.value));
              setCurrentPage(1);
            }}
            width="80px"
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </Select>
          <Box>entries</Box>
        </Box>

        <Box display="flex" alignItems="center" gap={2}>
          <Box>Search:</Box>
          <Input
            value={searchText}
            onChange={e => {
              setSearchText(e.target.value);
              setCurrentPage(1);
            }}
            placeholder="Search..."
            size="md"
            width="200px"
          />
        </Box>
      </Flex>
      <Box overflowX="auto">
        {filteredData.length === 0 ? (
          <Center py={8}>
            <Text fontSize="lg">No sightseen entries found for this area. Add one to get started.</Text>
          </Center>
        ) : (
          <Table variant="simple" w="100%">
            <Thead bg="#002B4D">
              <Tr>
                <Th color="white">#</Th>
                <Th color="white">Heading</Th>
                <Th color="white">Content</Th>
                <Th color="white">Image</Th>
                <Th color="white">Closing Time</Th>
                <Th color="white">Entry Fee</Th>
                <Th color="white">Action</Th>
              </Tr>
            </Thead>
            <Tbody>
              {currentEntries.map((item, idx) => (
                <Tr key={item.id || idx}>
                  <Td>{indexOfFirstEntry + idx + 1}</Td>
                  <Td>{item.heading}</Td>
                  <Td>
                    <div dangerouslySetInnerHTML={createMarkup(item.content)} style={{ maxWidth: "200px", overflow: "hidden", textOverflow: "ellipsis", maxHeight: "80px" }} />
                  </Td>
                  <Td>
                    {item.image ? (
                      <Image 
                        src={`${process.env.REACT_APP_IMG_URI}/${item.image}`} 
                        alt={item.heading}
                        boxSize="60px"
                        objectFit="cover"
                        borderRadius="md"
                      />
                    ) : (
                      <Box bg="gray.200" p={2} textAlign="center">No Image</Box>
                    )}
                  </Td>
                  <Td>{item.closingTime}</Td>
                  <Td>{item.entryFee}</Td>
                  <Td>
                    <Menu>
                      <MenuButton as={Button} colorScheme="green" size="sm">Action</MenuButton>
                      <MenuList>
                        <MenuItem>Delete</MenuItem>
                      </MenuList>
                    </Menu>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        )}
        
        {/* Pagination */}
        {filteredData.length > 0 && (
          <Flex justify="space-between" align="center" mt={4} fontSize="sm">
            <Box>
              Showing {indexOfFirstEntry + 1} to {Math.min(indexOfLastEntry, filteredData.length)} of {filteredData.length} entries
            </Box>
            <HStack spacing={1}>
              <Button 
                size="sm" 
                onClick={() => handlePageChange(1)} 
                disabled={currentPage === 1}
                colorScheme={currentPage === 1 ? "gray" : "blue"}
              >
                First
              </Button>
              <Button 
                size="sm" 
                onClick={() => handlePageChange(currentPage - 1)} 
                disabled={currentPage === 1}
                colorScheme={currentPage === 1 ? "gray" : "blue"}
              >
                Previous
              </Button>
              
              {/* Page buttons */}
              {[...Array(Math.min(5, totalPages))].map((_, i) => {
                const pageToShow = currentPage > 3 ? 
                  // If current page is > 3, show a range around current page
                  (i + currentPage - 2 + (currentPage + 2 > totalPages ? totalPages - currentPage - 2 : 0)) :
                  // Otherwise show first 5 pages
                  i + 1;
                
                if (pageToShow <= totalPages) {
                  return (
                    <Button
                      key={i}
                      size="sm"
                      onClick={() => handlePageChange(pageToShow)}
                      colorScheme={currentPage === pageToShow ? "blue" : "gray"}
                    >
                      {pageToShow}
                    </Button>
                  );
                }
                return null;
              })}
              
              <Button 
                size="sm" 
                onClick={() => handlePageChange(currentPage + 1)} 
                disabled={currentPage === totalPages}
                colorScheme={currentPage === totalPages ? "gray" : "blue"}
              >
                Next
              </Button>
              <Button 
                size="sm" 
                onClick={() => handlePageChange(totalPages)} 
                disabled={currentPage === totalPages}
                colorScheme={currentPage === totalPages ? "gray" : "blue"}
              >
                Last
              </Button>
            </HStack>
          </Flex>
        )}
      </Box>
    </Box>
  );
};

export default Sightseen;
